import { useEffect, useMemo, useRef, useState } from 'react';

import {
  filterOutColumns,
  getColumnEffectiveKey,
  getColumnLabel,
  type ISelectItemProps,
} from '@client/utils/table/column.util';

import type { TColumns } from '@client/types/antd.type';
// eslint-disable-next-line no-duplicate-imports
import type React from 'react';

/**
 * Custom hook to manage and filter columns in a table.
 * @param {TColumns[]} columns - Array of column objects to be processed.
 * @param {Array<TColumns['key']>} hiddenByDefault - Array of column keys to be hidden by default.
 * @returns {{
 *   columnsList: ISelectItemProps[],
 *   selectedColumns: TColumns['key'][],
 *   setSelectedColumns: React.Dispatch<React.SetStateAction<TColumns['key'][]>>,
 *   filteredColumns: TColumns[]
 * }} - Returns an object containing:
 *   - `columnsList`: List of selectable columns with label and value.
 *   - `selectedColumns`: Array of keys for selected columns.
 *   - `setSelectedColumns`: Function to update selected columns.
 *   - `filteredColumns`: Array of columns filtered based on selection.
 */
export const useColumnsToggle = <T extends object>(
  columns: TColumns<T>[],
  hiddenByDefault: Array<TColumns<T>['key']> = [],
): {
  columnsList: ISelectItemProps[];
  selectedColumns: TColumns<T>['key'][];
  setSelectedColumns: React.Dispatch<
    React.SetStateAction<TColumns<T>['key'][]>
  >;
  filteredColumns: TColumns<T>[];
} => {
  const [selectedColumns, setSelectedColumns] = useState<TColumns<T>['key'][]>(
    [],
  );
  const initializedRef = useRef(false);

  const columnsList = useMemo<ISelectItemProps[]>(
    () =>
      columns
        .filter((col: TColumns<T>) => col.concealable)
        .map((col: TColumns<T>) => ({
          label: getColumnLabel<T>(col),
          value: getColumnEffectiveKey<T>(col),
        })),
    [columns],
  );

  // Initialize only once
  useEffect(() => {
    if (!initializedRef.current && columnsList.length > 0) {
      const hiddenSet = new Set(hiddenByDefault.map(String));
      const concealable = columnsList.map((c) => c.value);

      const isHidden = (key: string) => {
        if (hiddenSet.has(key)) return true;
        const last = String(key).split('.').pop();
        return !!last && hiddenSet.has(last);
      };

      setSelectedColumns(concealable.filter((c) => !isHidden(c)));
      initializedRef.current = true;
    }
  }, [columnsList, hiddenByDefault]);

  const concealableKeys = useMemo<TColumns<T>['key'][]>(
    () => columnsList.map((c) => c.value),
    [columnsList],
  );

  const prevKeysRef = useRef<string[]>([]);

  // Handle dynamic column changes (columns being added/removed)
  useEffect(() => {
    if (!initializedRef.current) return; // Skip until initialization is done

    const prev = prevKeysRef.current;
    const keys = concealableKeys;
    const prevSet = new Set(prev);
    const keysSet = new Set(keys);

    const changed =
      prev.length !== keys.length ||
      keys.some((k) => !prevSet.has(k)) ||
      prev.some((k) => !keysSet.has(k));

    if (changed) {
      const addedKeys = keys.filter((k) => !prevSet.has(k));

      setSelectedColumns((sel) => {
        // Remove keys that no longer exist
        const next = sel.filter((v) => keysSet.has(String(v)));

        // Add new keys that aren't hidden by default (exact or by last segment)
        const hiddenSet = new Set(hiddenByDefault.map(String));
        const isHidden = (key: string) => {
          if (hiddenSet.has(key)) return true;
          const last = String(key).split('.').pop();
          return !!last && hiddenSet.has(last);
        };

        for (const k of addedKeys) {
          if (!isHidden(k) && !next.includes(k)) {
            next.push(k);
          }
        }
        return next;
      });

      prevKeysRef.current = keys;
    }
  }, [concealableKeys, hiddenByDefault]);

  const filteredColumns = useMemo<TColumns<T>[]>(
    () => filterOutColumns(columns, selectedColumns) as TColumns<T>[],
    [columns, selectedColumns],
  );

  return {
    columnsList,
    selectedColumns,
    setSelectedColumns,
    filteredColumns,
  };
};
